import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";

const AUTH_IMG = "https://images.unsplash.com/photo-1614064548237-096f735f344f";

export default function RegisterPage() {
  const { register } = useAuth();
  const { t, translateError } = useLanguage();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(email, password, name);
      navigate("/wiki", { replace: true });
    } catch (err) {
      setError(translateError(formatApiError(err.response?.data?.detail)) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="relative z-10 grid min-h-[calc(100vh-73px)] grid-cols-1 lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm animate-rise">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">{t("register.eyebrow")}</span>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter">{t("register.title")}</h1>

          <div data-testid="register-bonus" className="mt-4 flex items-center gap-2 border border-credit/40 bg-credit/10 px-4 py-3 text-sm font-semibold text-credit">
            {t("register.bonus")}
          </div>

          {error && (
            <div data-testid="register-error" className="mt-6 border border-lie/40 bg-lie/10 px-4 py-3 text-sm text-lie">{error}</div>
          )}

          <div className="mt-6 space-y-5">
            <div>
              <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("register.name")}</Label>
              <Input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required className="border-white/15 bg-card" />
            </div>
            <div>
              <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("register.email")}</Label>
              <Input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="border-white/15 bg-card" />
            </div>
            <div>
              <Label className="mb-2 block font-mono text-xs uppercase tracking-widest text-muted-foreground">{t("register.password")}</Label>
              <Input data-testid="register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="border-white/15 bg-card" />
            </div>
          </div>

          <Button type="submit" disabled={loading} data-testid="register-submit" className="mt-8 w-full rounded-md bg-primary py-6 font-bold uppercase tracking-wider hover:bg-[#FF645A]">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t("register.submit")}
          </Button>

          <p className="mt-6 text-sm text-muted-foreground">
            {t("register.hasAccount")}{" "}
            <Link to="/login" className="text-primary hover:underline">{t("register.login")}</Link>
          </p>
        </form>
      </div>

      <div className="relative hidden lg:block">
        <img src={AUTH_IMG} alt="" className="h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-l from-[#0A0A0A] via-transparent to-[#0A0A0A]" />
        <div className="absolute bottom-12 right-12 max-w-sm text-right">
          <h2 className="font-heading text-3xl font-black tracking-tighter">{t("register.sideTitle")}</h2>
          <p className="mt-3 text-muted-foreground">{t("register.sideText")}</p>
        </div>
      </div>
    </main>
  );
}
